import { HtmlContent } from '@app/react/components/HtmlContent';
import { t } from '@app/react/utils/i18n';
import { appConfig } from 'config/appConfig';
import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Button, Checkbox, Dismissible, Modal, Reshaped, Text, View } from 'reshaped/bundle';

export interface ModalResult {
  /** True only when the OK button was clicked. */
  confirmed: boolean;
  checkboxChecked: boolean;
}

/** `critical` for destructive actions like deleting or deactivating, `positive` for approving or reactivating. */
export type ModalOkColor = 'primary' | 'critical' | 'positive';

interface ShowModalOptions {
  title: string;
  /** Rendered as sanitized HTML, so translations may contain markup. */
  text: string;
  okLabel?: string;
  okColor?: ModalOkColor;
  /** Shows a cancel button if set */
  cancelLabel?: string;
  /** Shows a checkbox whose state is passed back in the result. */
  checkboxLabel?: string;
}

/**
 * Opens a modal outside any React tree (e.g. from Angular code) and resolves once it is closed.
 * Replaces the deprecated Angular `TextDialogComponent`.
 */
export function showModal(options: ShowModalOptions): Promise<ModalResult> {
  const container = document.createElement('div');
  document.body.appendChild(container);
  const root = createRoot(container);

  return new Promise((resolve) => {
    function close(result: ModalResult) {
      root.unmount();
      container.remove();
      resolve(result);
    }

    root.render(
      <Reshaped theme={appConfig.theme ?? ''} scoped>
        <ModalContent {...options} onClose={close} />
      </Reshaped>,
    );
  });
}

interface ModalContentProps extends ShowModalOptions {
  onClose: (result: ModalResult) => void;
}

export function ModalContent({
  title,
  text,
  okLabel = t('Global.Ok'),
  okColor = 'primary',
  cancelLabel,
  checkboxLabel,
  onClose,
}: Readonly<ModalContentProps>) {
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const close = (confirmed: boolean) => onClose({ confirmed, checkboxChecked });

  // Escape cancels. It is handled here, in the capture phase, so it never reaches the window listener of an
  // ngx-bootstrap modal underneath which would otherwise close itself as well.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') {
        return;
      }
      event.stopImmediatePropagation();
      onClose({ confirmed: false, checkboxChecked });
    }

    globalThis.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => globalThis.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [onClose, checkboxChecked]);

  return (
    <Modal
      active={true}
      onClose={() => close(false)}
      disableCloseOnOutsideClick={Boolean(cancelLabel)}
      position="center"
      size="480px"
      padding={5}>
      <View gap={4}>
        <Dismissible onClose={() => close(false)} closeAriaLabel={t('Global.Close')}>
          <Modal.Title>{title}</Modal.Title>
          <hr />
        </Dismissible>

        <Text>
          <HtmlContent html={text} />
        </Text>

        {checkboxLabel && (
          <Checkbox
            name="modalCheckbox"
            checked={checkboxChecked}
            onChange={({ checked }) => setCheckboxChecked(checked)}>
            {checkboxLabel}
          </Checkbox>
        )}

        <View direction="row" justify="end" gap={2}>
          {cancelLabel && (
            <Button variant="ghost" onClick={() => close(false)}>
              {cancelLabel}
            </Button>
          )}
          <Button color={okColor} variant="solid" onClick={() => close(true)}>
            {okLabel}
          </Button>
        </View>
      </View>
    </Modal>
  );
}
